import { DISHES, type DishKind } from "../game/dishes";
import type { Hint } from "../game/kitchen";
import {
  DRINK_NAMES,
  FULL_AT,
  SPILL_AT,
  TOPPING_NAMES,
  describeBuild,
  friesDoneness,
  friesStatus,
  glassStatus,
  pattyDoneness,
  pattyStatus,
  type Build,
  type Contents,
  type CookStatus,
} from "../game/food";
import { describeCoffee, heatStatus, type CoffeeBuild, type CoffeeCup } from "../game/coffee";
import { boardBasket, clockTime, closing, cupPlan, dishesOut, matchingCoffee, whyNotCoffee, drinkReady, machineStatus, matchingTicket, setupKinds, whyNot, type Cup, type ShiftState, type Ticket } from "../game/shift";
import { DishIcon } from "./DishIcon";
import { FoodIcon } from "./FoodIcon";
import { money } from "./format";

// The heads-up display over the kitchen: what the front of house needs
// (tickets, the shelf), how the pit's doing (tub, machine), and what Use will
// do right now.

/** Dish kinds the waiting tickets need more of than the shelf has. */
export function shortKinds(s: ShiftState): Set<DishKind> {
  const need: Partial<Record<DishKind, number>> = {};
  for (const t of s.tickets) for (const k of t.dishes) need[k] = (need[k] ?? 0) + 1;
  return new Set(setupKinds(s.setup).filter((k) => (need[k] ?? 0) > (s.shelf[k] ?? 0)));
}

const TOAST_SECONDS = 3.5;
/** Order-out toasts go faster, and only the latest few show: the good news shouldn't bury the bad. */
const SERVED_TOAST_SECONDS = 2;
const SERVED_TOASTS = 2;
const TICKETS_SHOWN = 3;

export function Hud({
  name,
  state,
  hint,
  touch,
  onPause,
}: {
  name: string;
  state: ShiftState;
  hint: Hint | null;
  touch: boolean;
  onPause: () => void;
}) {
  const { setup } = state;
  const short = shortKinds(state);
  const tubNearlyFull = state.tub.length >= setup.tubCapacity - 2;
  const machine = machineStatus(state);
  const problems = state.events.filter((e) => e.kind !== "served" && e.at > state.t - TOAST_SECONDS);
  const served = state.events.filter((e) => e.kind === "served" && e.at > state.t - SERVED_TOAST_SECONDS).slice(-SERVED_TOASTS);
  const toasts = [...served, ...problems];
  const canAct = hint?.action != null;
  // The order being built: in your hands, or on the board.
  const building = state.held?.kind === "basket" ? state.held.basket.contents : (boardBasket(state)?.contents ?? null);
  const coffee = state.held?.kind === "coffee" ? state.held.cup : (state.bar ?? null);

  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col text-panel">
      <header className="bg-ink/75 pt-[env(safe-area-inset-top,0px)] backdrop-blur-sm">
        <div className="flex items-center gap-3 px-3 py-1.5">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-bold opacity-90">{name}</p>
            <p className="text-sm font-bold tabular-nums">
              <span aria-label="Time">{clockTime(setup, state.t)}</span>
              {closing(state) ? (
                <span className="ml-1.5 rounded bg-sunny px-1.5 text-ink" aria-label="Closing up">
                  Closing up · {state.tickets.length ? `${state.tickets.length} ${state.tickets.length === 1 ? "order" : "orders"} to finish · ` : ""}
                  {dishesOut(state)} {dishesOut(state) === 1 ? "dish" : "dishes"} to put away
                </span>
              ) : (
                <span className="font-normal opacity-70"> · closes {clockTime(setup, setup.shiftSeconds)}</span>
              )}
            </p>
          </div>
          <p className="text-right">
            <span className="block text-[0.65rem] uppercase tracking-wide opacity-70">Tips</span>
            <span key={state.stats.served} className="inline-block animate-pop font-bold tabular-nums motion-reduce:animate-none" aria-label="Tips">
              {money(state.stats.tips)}
            </span>
          </p>
          <button
            type="button"
            onClick={onPause}
            aria-label="Pause"
            className="pointer-events-auto rounded-lg bg-panel/15 p-2.5 hover:bg-panel/25"
          >
            <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true" className="fill-panel">
              <rect x="3" y="2" width="4" height="14" rx="1" />
              <rect x="11" y="2" width="4" height="14" rx="1" />
            </svg>
          </button>
        </div>
        <div className="h-1 bg-ink-soft" aria-hidden="true">
          <div className="h-full bg-sunny" style={{ width: `${Math.min(1, state.t / setup.shiftSeconds) * 100}%` }} />
        </div>
      </header>

      <div className="flex flex-wrap items-start justify-between gap-2 p-2">
        <section aria-label="Tickets" className="flex min-w-0 flex-col gap-1.5">
          {state.tickets.length === 0 ? (
            <p className="rounded-lg bg-ink/60 px-2 py-1 text-xs">
              {state.stats.orders > 0 ? "Every order's out." : state.tub.length ? "No tickets yet. Get ahead on the tub." : "No tickets yet. The first customer's on the way."}
            </p>
          ) : (
            <>
              {state.tickets.slice(0, TICKETS_SHOWN).map((t) => (
                <TicketChip key={t.id} ticket={t} state={state} building={building} />
              ))}
              {state.tickets.length > TICKETS_SHOWN && (
                <span className="w-fit rounded-lg bg-ink/60 px-2 py-1 text-xs font-bold">+{state.tickets.length - TICKETS_SHOWN} more waiting</span>
              )}
            </>
          )}
        </section>

        <div className="ml-auto flex flex-col items-end gap-1.5">
          <section aria-label="Clean shelf" className="flex gap-1 rounded-lg bg-ink/60 p-1">
            {setupKinds(setup).map((k) => {
              const n = state.shelf[k] ?? 0;
              return (
                <span
                  key={k}
                  aria-label={`${n} clean ${n === 1 ? DISHES[k].name.toLowerCase() : DISHES[k].plural}`}
                  className={`flex items-center gap-0.5 rounded-md px-1 py-0.5 text-xs font-bold tabular-nums ${short.has(k) ? "bg-danger" : ""}`}
                >
                  <DishIcon kind={k} size={20} />
                  {n}
                </span>
              );
            })}
          </section>
          {(setup.kitchen || setup.cafe) && <LineStatus state={state} />}
          {state.ledge.length > 0 && (
            <section aria-label="On the ledge" className="flex items-center gap-1 rounded-lg bg-ink/60 px-1.5 py-1 text-xs font-bold">
              Ledge
              {state.ledge.map((c, i) => (
                <span key={i} aria-label={DRINK_NAMES[c.drink!]}>
                  <FoodIcon food={c.drink!} size={18} />
                </span>
              ))}
            </section>
          )}
          <div className="flex gap-1 text-xs font-bold">
            <span className={`rounded-lg px-2 py-1 tabular-nums ${tubNearlyFull ? "bg-danger" : "bg-ink/60"}`}>
              {setup.service === "cafe" ? "Bin" : "Tub"} {state.tub.length}/{setup.tubCapacity}
            </span>
            {setup.rackSlots > 0 && (
              <span className={`rounded-lg px-2 py-1 tabular-nums ${machine === "done" ? "bg-sunny text-ink" : "bg-ink/60"}`}>
                {machine === "washing" ? `Washing ${Math.ceil(state.machine!.doneAt - state.t)}s` : machine === "done" ? "Rack's clean!" : `Rack ${state.rack.length}/${setup.rackSlots}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {building && <BuildCard contents={building} state={state} />}
      {coffee && <CoffeeCard cup={coffee} state={state} />}
      {state.held?.kind === "cup" && <CupCard cup={state.held.cup} state={state} />}

      <div className="relative flex-1">
        <div
          aria-hidden="true"
          className={`absolute top-1/2 left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${canAct ? "border-sunny bg-sunny/40" : "border-panel/80"}`}
        />
        {hint && (
          <p
            role="status"
            className={`absolute top-1/2 left-1/2 mt-5 -translate-x-1/2 rounded-full px-3 py-1 text-sm font-bold whitespace-nowrap shadow ${canAct ? "bg-panel text-ink" : "bg-ink/70 text-panel"}`}
          >
            {canAct && !touch && <kbd className="mr-1.5 rounded border border-ink-soft px-1 font-mono text-xs">E</kbd>}
            {hint.label}
            {hint.action?.type === "scrub" && state.sink && <ScrubBar left={state.sink.grime - (state.sink.scrubbed ?? 0)} of={state.sink.maxGrime} />}
            {hint.action?.type === "pour" && state.held?.kind === "cup" && <FillBar fill={state.held.cup.fill} />}
            {hint.action?.type === "grind" && <FillBar fill={state.held?.kind === "portafilter" ? state.held.dose : 0} />}
          </p>
        )}
      </div>

      <div
        aria-live="polite"
        className={`flex flex-col items-center gap-2 px-3 ${touch ? "pb-[calc(env(safe-area-inset-bottom,0px)+11rem)]" : "pb-[calc(env(safe-area-inset-bottom,0px)+1rem)]"}`}
      >
        {toasts.map((e) =>
          e.kind === "served" ? (
            <p key={e.id} className="animate-pop rounded-full bg-accent px-3 py-1 text-center text-xs font-bold shadow motion-reduce:animate-none">
              {e.text} out · +{money(e.tip ?? 0)}
            </p>
          ) : (
            <p key={e.id} className="animate-pop rounded-full bg-danger px-4 py-2 text-center text-sm font-bold shadow-lg motion-reduce:animate-none">
              {e.text}
            </p>
          ),
        )}
      </div>
    </div>
  );
}

function TicketChip({ ticket, state, building }: { ticket: Ticket; state: ShiftState; building: Contents | null }) {
  const left = Math.max(0, 1 - (state.t - ticket.placedAt) / state.setup.patience);
  const bar = left > 0.5 ? "bg-accent" : left > 0.25 ? "bg-sunny" : "bg-danger";
  if (ticket.coffee) {
    const cup = state.held?.kind === "coffee" ? state.held.cup : state.bar;
    const ready = !!cup && matchingCoffee(state, cup)?.id === ticket.id;
    return (
      <div
        className={`w-44 animate-pop rounded-lg bg-panel px-2 py-1.5 text-ink shadow motion-reduce:animate-none sm:w-52 ${ready ? "ring-4 ring-sunny" : ""}`}
        aria-label={`Order ${ticket.no}, ${ticket.name}'s ${ticket.item}: ${describeCoffee(ticket.coffee)}${ready ? " (ready)" : ""}`}
      >
        <p className="flex items-baseline gap-1.5 leading-tight">
          <span className="text-base font-black tabular-nums">#{ticket.no}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-bold">{ticket.name}</span>
          {ready && <span className="rounded bg-sunny px-1 text-[0.65rem] font-black">READY</span>}
        </p>
        <p className="truncate text-xs font-semibold text-ink-soft">{ticket.item}</p>
        <CoffeeIcons build={ticket.coffee} />
        <div className="h-1.5 overflow-hidden rounded-full bg-steel">
          <div className={`h-full ${bar}`} style={{ width: `${left * 100}%` }} />
        </div>
      </div>
    );
  }
  if (ticket.build) {
    const ready = !!building && matchingTicket(state, building)?.id === ticket.id;
    return (
      <div
        className={`w-44 animate-pop rounded-lg bg-panel px-2 py-1.5 text-ink shadow motion-reduce:animate-none sm:w-52 ${ready ? "ring-4 ring-sunny" : ""}`}
        aria-label={`Order ${ticket.no}, ${ticket.item}: ${describeBuild(ticket.build)}${ready ? " (ready)" : ""}`}
      >
        <p className="flex items-baseline gap-1.5 leading-tight">
          <span className="text-base font-black tabular-nums">#{ticket.no}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-bold">{ticket.item}</span>
          {ready && <span className="rounded bg-sunny px-1 text-[0.65rem] font-black">READY</span>}
        </p>
        <BuildIcons build={ticket.build} drinkOnLedge={!!ticket.build.drink && drinkReady(state, ticket.build.drink)} />
        <div className="h-1.5 overflow-hidden rounded-full bg-steel">
          <div className={`h-full ${bar}`} style={{ width: `${left * 100}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="w-20 shrink-0 animate-pop rounded-md bg-panel p-1 text-ink shadow motion-reduce:animate-none" aria-label={`${ticket.item}: needs ${ticket.dishes.map((k) => DISHES[k].name.toLowerCase()).join(", ")}`}>
      <p className="truncate text-[0.65rem] font-bold leading-tight">{ticket.item}</p>
      <p className="my-0.5 flex gap-0.5">
        {ticket.dishes.map((k, i) => (
          <span key={i} className={(state.shelf[k] ?? 0) > 0 ? "" : "opacity-35"}>
            <DishIcon kind={k} size={16} />
          </span>
        ))}
      </p>
      <div className="h-1 overflow-hidden rounded-full bg-steel">
        <div className={`h-full ${bar}`} style={{ width: `${left * 100}%` }} />
      </div>
    </div>
  );
}

/** A ticket's build as icons with their names: what goes in the basket. */
function BuildIcons({ build, drinkOnLedge }: { build: Build; drinkOnLedge: boolean }) {
  const items: { key: string; food: Parameters<typeof FoodIcon>[0]["food"]; label: string }[] = [];
  if (build.patties) items.push({ key: "patty", food: "patty", label: build.patties > 1 ? `${build.patties}× patty` : "patty" });
  for (const t of build.toppings) items.push({ key: t, food: t, label: TOPPING_NAMES[t] });
  if (build.fries) items.push({ key: "fries", food: "fries", label: "fries" });
  if (build.drink) items.push({ key: "drink", food: build.drink, label: `${DRINK_NAMES[build.drink]}${drinkOnLedge ? " ✓" : ""}` });
  return (
    <ul className="my-1 flex flex-wrap gap-x-1.5 gap-y-0.5 text-xs font-semibold">
      {items.map((i) => (
        <li key={i.key} className="flex items-center gap-0.5">
          <FoodIcon food={i.food} size={18} />
          {i.label}
        </li>
      ))}
      {build.patties > 0 && !build.toppings.length && <li className="self-center text-ink-soft">plain</li>}
    </ul>
  );
}

const STATUS_TEXT: Record<CookStatus, string> = { cooking: "", flip: "Flip!", done: "Done", burning: "Burning!", burnt: "Burnt" };
const STATUS_STYLE: Record<CookStatus, string> = {
  cooking: "bg-ink/60",
  flip: "bg-sunny text-ink animate-pulse motion-reduce:animate-none",
  done: "bg-accent",
  burning: "bg-danger animate-pulse motion-reduce:animate-none",
  burnt: "bg-danger",
};

/** The flat-top and fryer at a glance, wherever you're standing: what's cooking, and what needs you now. */
function LineStatus({ state }: { state: ShiftState }) {
  const spots = [
    ...state.griddle.map((p, i) => p && { key: `g${i}`, food: "patty" as const, name: `Patty ${i + 1}`, ...pattyStatus(p) }),
    ...state.fryer.map((f, i) => f && { key: `f${i}`, food: "fries" as const, name: `Fries ${i + 1}`, ...friesStatus(f) }),
    ...state.groups.map((g, i) => g && { key: `s${i}`, food: "shot" as const, name: `Shot ${i + 1}`, ...heatStatus(g) }),
    ...(state.wand ? [{ key: "w", food: "milk" as const, name: "Milk", ...heatStatus(state.wand) }] : []),
  ].filter((x) => !!x);
  if (!spots.length) return null;
  return (
    <section aria-label="On the heat" className="flex flex-wrap justify-end gap-1">
      {spots.map((x) => (
        <span
          key={x.key}
          aria-label={`${x.name}: ${x.status === "cooking" ? `cooking, ${Math.round(x.progress * 100)}%` : STATUS_TEXT[x.status].replace("!", "").toLowerCase()}`}
          className={`flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-bold ${STATUS_STYLE[x.status]}`}
        >
          <FoodIcon food={x.food} size={18} />
          {x.status === "cooking" ? (
            <span className="block h-1.5 w-8 overflow-hidden rounded-full bg-steel-dark" aria-hidden="true">
              <span className="block h-full bg-panel" style={{ width: `${x.progress * 100}%` }} />
            </span>
          ) : (
            STATUS_TEXT[x.status]
          )}
        </span>
      ))}
    </section>
  );
}

/** What's in the basket being built, and whether it's ready for a ticket or what it still needs. */
function BuildCard({ contents, state }: { contents: Contents; state: ShiftState }) {
  const parts: string[] = [];
  contents.patties.forEach((p) => {
    const d = pattyDoneness(p);
    parts.push(d === "done" ? "patty" : d === "burnt" ? "burnt patty" : "raw patty");
  });
  parts.push(...contents.toppings.map((t) => TOPPING_NAMES[t]));
  if (contents.fries) {
    const d = friesDoneness(contents.fries);
    parts.push(d === "done" ? "fries" : d === "burnt" ? "burnt fries" : "soggy fries");
  }
  const why = whyNot(state, contents);
  return (
    <section aria-label="The order you're building" className="mx-2 w-fit max-w-[calc(100%-1rem)] rounded-lg bg-ink/75 px-2 py-1 text-xs">
      <p>
        <span className="font-bold">Basket:</span> {parts.length ? parts.join(", ") : "empty"}
      </p>
      <p className={`font-bold ${why ? "text-sunny-soft" : "text-accent-soft"}`}>{why ?? `Ready: ${matchingTicket(state, contents)!.item}. Take it to the window`}</p>
    </section>
  );
}

/** A coffee ticket's build as icons: its shots, the milk, the syrup. */
function CoffeeIcons({ build }: { build: CoffeeBuild }) {
  return (
    <ul className="my-1 flex flex-wrap gap-x-1.5 gap-y-0.5 text-xs font-semibold">
      <li className="flex items-center gap-0.5">
        <FoodIcon food="shot" size={18} />
        {build.shots > 1 ? `${build.shots}× shot` : "shot"}
      </li>
      {build.milk && (
        <li className="flex items-center gap-0.5">
          <FoodIcon food="milk" size={18} />
          milk
        </li>
      )}
      {build.syrup && (
        <li className="flex items-center gap-0.5">
          <FoodIcon food={build.syrup} size={18} />
          {build.syrup}
        </li>
      )}
    </ul>
  );
}

/** The coffee being made, on the bar or in your hands: what's in it, and what it still needs. */
function CoffeeCard({ cup, state }: { cup: CoffeeCup; state: ShiftState }) {
  const parts = [
    ...cup.shots.map((s) => (heatStatus(s).status === "cooking" ? "sour shot" : heatStatus(s).status === "burnt" ? "bitter shot" : "shot")),
    ...(cup.milk ? [heatStatus(cup.milk).status === "cooking" ? "cold milk" : heatStatus(cup.milk).status === "burnt" ? "scalded milk" : "steamed milk"] : []),
    ...cup.syrups,
  ];
  const why = whyNotCoffee(state, cup);
  const ready = matchingCoffee(state, cup);
  return (
    <section aria-label="The coffee you're making" className="mx-2 w-fit max-w-[calc(100%-1rem)] rounded-lg bg-ink/75 px-2 py-1 text-xs">
      <p>
        <span className="font-bold">Cup:</span> {parts.length ? parts.join(", ") : "empty"}
      </p>
      <p className={`font-bold ${why ? "text-sunny-soft" : "text-accent-soft"}`}>{why ?? `Ready: ${ready!.name}'s ${ready!.item.toLowerCase()}. Take it to the window`}</p>
    </section>
  );
}

/** The cup in your hands: what's in it, how full, and where it goes next. */
function CupCard({ cup, state }: { cup: Cup; state: ShiftState }) {
  const status = glassStatus(cup);
  const plan = cupPlan(state, cup);
  const what = cup.drink ? `${DRINK_NAMES[cup.drink]}, ${Math.round(Math.min(cup.fill, SPILL_AT) * 100)}% full` : "empty";
  const next =
    status === "empty" ? "Hold Use under a nozzle to pour" : status === "short" ? "Keep pouring to the line" : status === "spilled" ? "Spilling over! Tip it out" : plan.ticket ? `Ready: ${plan.ticket.item}. Take it to the window` : plan.ledge ? "Full! Set it on the window ledge" : plan.why!;
  return (
    <section aria-label="The drink you're pouring" className="mx-2 mt-1 w-fit max-w-[calc(100%-1rem)] rounded-lg bg-ink/75 px-2 py-1 text-xs">
      <p>
        <span className="font-bold">Cup:</span> {what}
      </p>
      <p className={`font-bold ${status === "full" ? "text-accent-soft" : "text-sunny-soft"}`}>{next}</p>
    </section>
  );
}

/** How full the cup is, with the band that counts as full marked: stop in it. */
function FillBar({ fill }: { fill: number }) {
  const over = fill > SPILL_AT;
  return (
    <span className="relative ml-2 inline-block h-2.5 w-20 overflow-hidden rounded-full bg-steel align-middle" aria-hidden="true">
      <span className="absolute inset-y-0 bg-accent-soft" style={{ left: `${FULL_AT * 100}%`, right: 0 }} />
      <span className={`absolute inset-y-0 left-0 ${over ? "bg-danger" : "bg-accent"}`} style={{ width: `${Math.min(1, fill) * 100}%` }} />
    </span>
  );
}

/** How much grime is left on the dish in the sink, draining as you scrub. */
function ScrubBar({ left, of }: { left: number; of: number }) {
  return (
    <span className="ml-2 inline-block h-2 w-14 overflow-hidden rounded-full bg-steel align-middle" aria-hidden="true">
      <span className="block h-full bg-accent" style={{ width: `${Math.max(0, Math.min(1, 1 - left / of)) * 100}%` }} />
    </span>
  );
}
